'use client'

import classnames from 'classnames'
import React from 'react'
import styles from './styles.module.css'
import {
  ComputeTransformStr,
  InterpObj,
  type CardAngle,
  type CardStyle,
} from './utils'
import {MWDSUnstyledButton} from '~/mercuryWebCompat/design-system/MWDSUnstyledButton'

type CarouselProps = {
  cards: React.ReactNode[]

  /* Width of the carousel container, in px. */
  itemWidth: number
  /* How large the carousel is, in px. */
  radius: number
  /* How much the card ring should be up and to the right, in degrees */
  tilt: number
  /* A function to specify how cards should rotate on the ring, if at all */
  cardRotation?: (ringAngle: number) => CardAngle
  /* default 5 - How fast cards will interp their position */
  animationSpeed?: number
  /*
  default 0.25 - How fast dragging will make cards rotate.
  Conversion between pixels and degrees (0.25 degrees per pixel dragged, for example).
  */
  dragSpeed?: number
  /* default 5 - How many degrees to rotate cards based on the cursor location */
  hoverRotationAmount?: number
  /* default 2 - The maximum blur() filter value in px for cards at the very back */
  blurStrength?: number
  /* default 0.4 - The minimum opacity for cards at the very back */
  minOpacity?: number
  /*
  default 1200 - Distance in pixels the cards should appear.
  Smaller values increase the distortion towards the viewer.
  Passed directly to https://developer.mozilla.org/en-US/docs/Web/CSS/transform-function/perspective
  */
  perspective?: number
  /*
  default 0 - If this is set, the carousel will spin a bit when scrolled into view
  */
  initialSpin?: number
  /* Allows additional styling of carousel buttons */
  carouselButtonsClassName?: string
  /* Index of currently featured testimonial slide */
  currentTestimonialIndex: number
  setCurrentTestimonialIndex: (index: number) => void
}

const TestimonialCarousel = (props: CarouselProps) => {
  const {
    cards,
    itemWidth,
    radius,
    tilt,
    cardRotation,
    animationSpeed,
    dragSpeed,
    hoverRotationAmount,
    blurStrength,
    minOpacity,
    perspective,
    initialSpin,
    carouselButtonsClassName,
    currentTestimonialIndex,
    setCurrentTestimonialIndex,
  } = props
  const angleInterval = 360 / cards.length

  const hoverLocation = React.useRef<[number, number]>([0.5, 0.5])
  const totalOffsetX = React.useRef(0)

  const selectItem = (index: number) => {
    setCurrentTestimonialIndex(index)
    hoverLocation.current = [0.5, 0.5]
    totalOffsetX.current = 0
  }
  const currentTransforms = React.useRef<CardStyle[]>([])
  const cardRefs = React.useRef<HTMLDivElement[]>([])
  const wrapperElementRef = React.createRef<HTMLDivElement>()

  const animateRef = React.useRef<number>()
  const lastTime = React.useRef<number>(0)

  const ComputeCardStyle = React.useCallback(
    (i: number, extraRingLocation?: number): CardStyle => {
      // The cards are placed on a ring that's tilted back and to the left.

      const theta =
        angleInterval * (i - currentTestimonialIndex) +
        totalOffsetX.current +
        (extraRingLocation ?? 0)

      // Tilt the ring this much downwards
      const ringAnglePhi = -tilt

      const ringRadius = radius

      // We use a spherical-cartesian transform to get the position on the ring in XYZ coords
      const x =
        ringRadius *
        Math.cos((Math.PI * ringAnglePhi) / 180) *
        Math.sin((Math.PI * theta) / 180)
      const y =
        ringRadius *
        Math.sin((Math.PI * ringAnglePhi) / 180) *
        Math.sin((Math.PI * theta) / 180)
      const z = ringRadius * Math.cos((Math.PI * theta) / 180) - ringRadius

      // The items are scaled according to their Z value, lerped between 1.0 and 0.7
      // We could use a perspective() transform but that yields field-of-view artifacts that would look strange
      const scale = 1 + (z / (ringRadius * 2)) * 0.3

      const ang: CardAngle = cardRotation
        ? cardRotation(theta)
        : {pitch: 0, yaw: 0, roll: 0}

      if (i === currentTestimonialIndex) {
        const cx = 2 * (hoverLocation.current[0] - 0.5)
        const cy = 2 * (hoverLocation.current[1] - 0.5)
        ang.pitch -= cy * (hoverRotationAmount ?? 5)
        ang.yaw += cx * (hoverRotationAmount ?? 5)
      }

      const opacity = 1 - (z / -(radius * 2)) * (1 - (minOpacity ?? 0.4))
      const blur = (z / -(radius * 2)) * (blurStrength ?? 2)
      const cursor = z > -5 ? 'initial' : 'pointer'

      return {
        transform: {
          perspective: perspective ?? 1200,
          scale,
          location: {
            x,
            y,
            z: z / 5,
          },
          rotation: ang,
        },
        opacity,
        blur,
        cursor,
      }
    },
    [
      angleInterval,
      currentTestimonialIndex,
      tilt,
      radius,
      cardRotation,
      hoverRotationAmount,
      minOpacity,
      blurStrength,
      perspective,
    ]
  )

  /*
    Why do we do this instead of just CSS animation?
    When animating with CSS, animations are computed by transitioning values from A -> B
    We want the animation to be smooth and organic; when dragging (for example) CSS animation
    appears very janky and stuttered.
  */
  const animate = React.useCallback(
    (time: number) => {
      const deltaTime = (time - lastTime.current) / 1000
      lastTime.current = time

      for (let i = 0; i < currentTransforms.current.length; i++) {
        currentTransforms.current[i] = InterpObj(
          currentTransforms.current[i],
          ComputeCardStyle(i),
          deltaTime,
          animationSpeed ?? 5
        )

        const card = cardRefs.current[i]

        if (card) {
          const style = currentTransforms.current[i]
          const transform = ComputeTransformStr(style)

          card.setAttribute(
            'style',
            `transform: ${transform}; opacity: ${style.opacity}; filter: blur(${style.blur}px); cursor: ${style.cursor};`
          )
        }
      }

      animateRef.current = requestAnimationFrame(animate)
    },
    [ComputeCardStyle, animationSpeed]
  )
  React.useEffect(() => {
    animateRef.current = requestAnimationFrame(animate)
    return () => {
      if (animateRef.current) {
        cancelAnimationFrame(animateRef.current)
      }
    }
  }, [animate])

  React.useEffect(() => {
    cardRefs.current = cardRefs.current.slice(0, cards.length)
    for (let i = 0; i < cards.length; i++) {
      if (currentTransforms.current.length <= i) {
        const style = ComputeCardStyle(i)
        currentTransforms.current.push(style)
      }
    }
  }, [ComputeCardStyle, angleInterval, cards.length, currentTestimonialIndex])

  /*
    We use a IntersectionObserver here to get the cards rotating when the user scrolls by
  */
  const [observed, setObserved] = React.useState(false)
  const intersection = React.useRef<IntersectionObserver>()
  React.useEffect(() => {
    if (wrapperElementRef.current && !observed) {
      intersection.current = new IntersectionObserver(
        entries => {
          if (entries[0]?.isIntersecting && !observed) {
            setObserved(true)
            for (let i = 0; i < currentTransforms.current.length; i++) {
              currentTransforms.current[i] = ComputeCardStyle(i, initialSpin)
            }
          }
        },
        {
          rootMargin: '-15% 0px',
        }
      )
      intersection.current.observe(wrapperElementRef.current)

      return () => intersection.current?.disconnect()
    }
  }, [
    ComputeCardStyle,
    angleInterval,
    observed,
    currentTestimonialIndex,
    wrapperElementRef,
    initialSpin,
  ])

  const dragging = React.useRef(false)
  const [isUserClickingOrDragging, setIsUserClickingOrDragging] = React.useState<
    'click' | 'drag'
  >('click')
  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    event.preventDefault()
    dragging.current = true
    lastDragX.current = event.changedTouches[0].screenX
    setIsUserClickingOrDragging('click')
  }
  const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    dragging.current = true
    lastDragX.current = event.screenX
    setIsUserClickingOrDragging('click')
  }

  const snapCardAfterDrag = () => {
    let rotatedToCard =
      -totalOffsetX.current / angleInterval + currentTestimonialIndex
    rotatedToCard = Math.round(rotatedToCard)
    while (rotatedToCard < 0) {
      rotatedToCard += cards.length
    }
    while (rotatedToCard >= cards.length) {
      rotatedToCard -= cards.length
    }
    selectItem(rotatedToCard)
  }

  const onTouchEnd = (event: React.UIEvent) => {
    if (dragging.current) {
      event.preventDefault()
      dragging.current = false
      snapCardAfterDrag()
    }
  }

  const lastDragX = React.useRef(0)
  const addDragOffset = (dx: number) => {
    totalOffsetX.current += dx * (dragSpeed ?? 0.25)
  }

  const onTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (dragging.current) {
      event.preventDefault()
      const dx = event.changedTouches[0].screenX - lastDragX.current
      lastDragX.current = event.changedTouches[0].screenX
      // If a user 'clicks' on a carousel item, we want to cycle to that item. If they are 'dragging' to cycle
      // through the carousel items, we want to snap to the nearest one. We also want some buffer in the `dx` check
      // to ensure a click event with small, accidental mouse movements is not mischaracterized.
      if (Math.abs(dx) > 1) {
        setIsUserClickingOrDragging('drag')
      }
      addDragOffset(dx)
    }
  }

  const onMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (dragging.current) {
      event.preventDefault()
      const dx = event.screenX - lastDragX.current
      lastDragX.current = event.screenX
      // If a user 'clicks' on a carousel item, we want to cycle to that item. If they are 'dragging' to cycle
      // through the carousel items, we want to snap to the nearest one. We also want some buffer in the `dx` check
      // to ensure a click event with small, accidental mouse movements is not mischaracterized.
      if (Math.abs(dx) > 1) {
        setIsUserClickingOrDragging('drag')
      }
      addDragOffset(dx)
    }

    if (!wrapperElementRef.current) {
      return
    }
    const rect = wrapperElementRef.current.getBoundingClientRect()
    const mx = event.clientX
    const my = event.clientY
    const xFrac = (mx - rect.left) / rect.width
    const yFrac = (my - rect.top) / rect.height
    hoverLocation.current = [xFrac, yFrac]
  }

  const onMouseLeave = () => {
    hoverLocation.current = [0.5, 0.5]
    if (dragging.current) {
      dragging.current = false
      snapCardAfterDrag()
    }
  }

  const carouselButtons = cards.map((_, index) => {
    return (
      <MWDSUnstyledButton
        key={index}
        analyticsEvent={{
          type: 'testimonials/button-click',
          customProperties: {buttonNumber: index + 1},
        }}
        aria-label={`Go to page ${index + 1}`}
        className={classnames(
          styles.carouselButton,
          currentTestimonialIndex === index ? styles.active : undefined
        )}
        onClick={() => {
          selectItem(index)
        }}
      >
        <div className={styles.buttonMarker} />
      </MWDSUnstyledButton>
    )
  })

  return (
    <div
      className={styles.carouselControls}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseUp={onTouchEnd}
      onTouchMove={onTouchMove}
    >
      <div className={classnames(styles.carouselButtons, carouselButtonsClassName)}>
        {carouselButtons}
      </div>
      <div className={styles.carouselContainer} ref={wrapperElementRef}>
        <div className={styles.carousel} style={{width: itemWidth}}>
          {cards.map((card, index) => {
            if (currentTransforms.current.length > index) {
              const style = currentTransforms.current[index]
              const transform = ComputeTransformStr(style)

              return (
                <div
                  key={index}
                  className={classnames(styles.carouselItem)}
                  style={{
                    transform,
                    opacity: style.opacity,
                    filter: `blur(${style.blur})px`,
                    cursor: style.cursor,
                  }}
                  onClick={() => {
                    if (isUserClickingOrDragging === 'click') {
                      selectItem(index)
                    }
                  }}
                  ref={el => {
                    if (el) {
                      cardRefs.current[index] = el
                    }
                  }}
                >
                  {card}
                </div>
              )
            } else {
              return <div key={index} />
            }
          })}
        </div>
      </div>
    </div>
  )
}
export default TestimonialCarousel
